import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, Loader2, Phone, Bell, X } from "lucide-react";

export default function FollowUps() {
  const { activeId } = useFacility();
  const [rows, setRows] = useState(null);
  const [days, setDays] = useState("30");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const dateMode = Boolean(fromDate || toDate);

  useEffect(() => {
    if (!activeId) return;
    setRows(null);
    const params = new URLSearchParams({ facility_id: activeId });
    if (dateMode) {
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
    } else {
      params.set("days", days);
    }
    api.get(`/opd/followups?${params.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [activeId, days, fromDate, toDate, dateMode]);

  const clearDates = () => { setFromDate(""); setToDate(""); };

  const daysUntil = (d) => { const diff = Math.ceil((new Date(d) - new Date()) / 86400000); return diff <= 0 ? "Due today" : `in ${diff} day${diff > 1 ? "s" : ""}`; };

  return (
    <div data-testid="followups-page">
      <PageHeader title="Follow-ups & Reminders" subtitle="Upcoming scheduled follow-up visits from consultations."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-slate-500">From</label>
              <input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700" data-testid="followup-from-date" />
            </div>
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-slate-500">To</label>
              <input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700" data-testid="followup-to-date" />
            </div>
            {dateMode && (
              <button onClick={clearDates} className="inline-flex items-center gap-1 h-9 px-2.5 rounded-md border border-slate-200 bg-white text-xs text-slate-600 hover:bg-slate-50" data-testid="followup-clear-dates"><X className="h-3.5 w-3.5" /> Clear dates</button>
            )}
            <Select value={days} onValueChange={setDays} disabled={dateMode}>
              <SelectTrigger className="w-[160px] bg-white" data-testid="followup-range"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="7">Next 7 days</SelectItem><SelectItem value="30">Next 30 days</SelectItem><SelectItem value="90">Next 90 days</SelectItem></SelectContent>
            </Select>
          </div>} />

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Follow-up date</TableHead><TableHead>Patient</TableHead><TableHead>Doctor</TableHead><TableHead>Advice</TableHead><TableHead>Contact</TableHead><TableHead>Reminder</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} className="hover:bg-slate-50" data-testid={`followup-row-${i}`}>
                  <TableCell><span className="inline-flex items-center gap-1.5 font-medium text-slate-800"><CalendarClock className="h-4 w-4 text-blue-600" />{r.follow_up_date}{r.follow_up_time ? ` · ${r.follow_up_time}` : ""}</span></TableCell>
                  <TableCell><div className="font-medium text-slate-800">{r.patient_name}</div><div className="text-xs text-slate-400 font-mono">{r.uhid}</div></TableCell>
                  <TableCell className="text-sm text-slate-600">{r.doctor_name || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-xs truncate">{r.follow_up_advice || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{r.mobile ? <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-slate-400" />{r.mobile}</span> : "—"}</TableCell>
                  <TableCell><span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200"><Bell className="h-3 w-3" />{daysUntil(r.follow_up_date)}</span></TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-12">No upcoming follow-ups in this window.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
